import React from 'react';
import {
  FileText, ClipboardList, Zap, FolderOpen, Briefcase,
  ChevronUp, Settings, Minus, Plus, Loader2,
} from 'lucide-react';
import HourglassIcon from './HourglassIcon';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { MOOD_THEMES } from '../../constants/mediscribe';

/**
 * FloatingActions — Sticky create-docs bar + Section nav FAB + Quick settings FAB.
 * Extracted from App.js to reduce monolith size.
 */
const FloatingActions = ({
  notes, result, isGenerating, generateDocuments,
  // Quick Settings
  currentMoodIndex, setCurrentMoodIndex,
  forceSingleColumn, toggleColumnLayout,
  textSizeLevel, setTextSizeLevel,
  includePatientQuotes, setIncludePatientQuotes,
  trackFeature,
  setShowProfileModal,
  effectiveRole,
  apFormat, setApFormat,
  clinicalTone, setClinicalTone,
}) => {
  // Patient theme indexes for Light/Dark/Hi-Con only
  const PATIENT_THEMES = [
    { id: 'clinical', label: 'Light' },
    { id: 'calm', label: 'Dark' },
    { id: 'high-contrast', label: 'Hi-Con' },
  ];

  return (
  <>
    {/* Sticky Create Docs Bar */}
    {notes.trim() && !result && (
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur-sm border-t border-border p-2 flex items-center justify-center gap-3 sm:hidden" data-testid="sticky-create-docs-bar">
        <Button
          onClick={generateDocuments}
          disabled={isGenerating || !notes?.trim()}
          className="flex-1 max-w-sm h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm"
          data-testid="sticky-create-docs-btn"
          title={isGenerating ? "Generating..." : "Generate"}
        >
          {isGenerating ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</>
          ) : (
            <><img src="/emblem_icon.png" alt="" className="w-5 h-5 mr-2 object-contain inline-block" />Generate</>
          )}
        </Button>
      </div>
    )}

    {/* Floating Action Buttons */}
    <div className="fixed bottom-20 left-3 z-40 flex flex-col gap-2" data-testid="fab-container">
      {/* Section Navigation Popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            size="icon"
            className="h-10 w-10 rounded-full shadow-lg bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 transition-all"
            title="Jump to Section"
            data-testid="section-nav-fab"
          >
            <ChevronUp className="w-6 h-6" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-52 p-2">
          <div className="space-y-1">
            <div className="font-medium text-xs text-muted-foreground px-2 pb-1">Jump to Section</div>
            {[
              { id: 'input-section', icon: <FileText className="w-4 h-4 text-primary" />, label: 'Your Notes' },
              { id: 'output-section', icon: <ClipboardList className="w-4 h-4 text-green-500" />, label: 'Your Documents' },
              { id: 'imaging-section', icon: <Zap className="w-4 h-4 text-yellow-500" />, label: 'Imaging Orders' },
              { id: 'referral-section', icon: <Briefcase className="w-4 h-4 text-blue-500" />, label: 'Referrals' },
              { id: 'letter-section', icon: <FileText className="w-4 h-4 text-purple-500" />, label: 'Letter / Consult' },
              { id: 'reference-section', icon: <FolderOpen className="w-4 h-4 text-amber-500" />, label: 'References' },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => {
                  const el = document.getElementById(s.id);
                  if (el) {
                    // If element is inside a collapsed section, open it first
                    const collapsibleContent = el.closest('[data-state="closed"]');
                    if (collapsibleContent) {
                      const trigger = collapsibleContent.previousElementSibling?.querySelector('[data-testid="footer-toggle"]') 
                        || document.querySelector('[data-testid="footer-toggle"]');
                      if (trigger) trigger.click();
                      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
                    } else {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  } else {
                    // Element might be in a collapsed footer — try opening it
                    const trigger = document.querySelector('[data-testid="footer-toggle"]');
                    if (trigger) {
                      trigger.click();
                      setTimeout(() => {
                        const delayed = document.getElementById(s.id);
                        if (delayed) delayed.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 400);
                    }
                  }
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-secondary transition-colors text-left"
              >
                {s.icon}
                {s.label}
              </button>
            ))}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-secondary transition-colors text-left"
            >
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
              Top of Page
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Quick settings popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-10 w-10 rounded-full shadow-lg bg-primary hover:bg-primary/90 transition-all"
            title="Quick Settings"
            data-testid="settings-fab"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-64 p-3">
          <div className="space-y-3">
            <div className="font-medium text-sm border-b pb-2">Quick Settings</div>

            {/* Theme/Mood — Full moods for providers, Light/Dark/Hi-Con for patients */}
            {effectiveRole === 'provider' ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Theme</span>
              <Select
                value={MOOD_THEMES[currentMoodIndex]?.id || 'neutral'}
                onValueChange={(val) => {
                  const idx = MOOD_THEMES.findIndex(m => m.id === val);
                  if (idx >= 0) { setCurrentMoodIndex(idx); trackFeature('mood_changer'); }
                }}
              >
                <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOOD_THEMES.map(m => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            ) : (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Theme</span>
              <div className="flex gap-1">
                {PATIENT_THEMES.map(t => {
                  const idx = MOOD_THEMES.findIndex(m => m.id === t.id);
                  const isActive = currentMoodIndex === idx;
                  return (
                    <Button key={t.id} variant={isActive ? 'default' : 'outline'} size="sm"
                      onClick={() => { if (idx >= 0) setCurrentMoodIndex(idx); }}
                      className="h-7 text-xs flex-1" data-testid={`fab-theme-${t.id}`}>
                      {t.label}
                    </Button>
                  );
                })}
              </div>
            </div>
            )}

            {/* Column Layout */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Layout</span>
              <Button variant="outline" size="sm" onClick={toggleColumnLayout} className="h-7 text-xs">
                {forceSingleColumn ? '1 Column' : '2 Columns'}
              </Button>
            </div>

            {/* Text Size */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Text Size</span>
              <div className="flex gap-1">
                <Button
                  variant="outline" size="sm"
                  onClick={() => { if (textSizeLevel > 0) { setTextSizeLevel(textSizeLevel - 1); localStorage.setItem('writeright_text_size', String(textSizeLevel - 1)); } }}
                  className="h-7 w-7 p-0"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => { if (textSizeLevel < 2) { setTextSizeLevel(textSizeLevel + 1); localStorage.setItem('writeright_text_size', String(textSizeLevel + 1)); } }}
                  className="h-7 w-7 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Dictation Quotes Style */}
            <div className="space-y-1.5 pt-2 border-t border-border">
              <span className="text-xs text-muted-foreground">Patient's Own Words</span>
              <div className="flex gap-1">
                {[
                  { value: 'none', label: 'None' },
                  { value: 'summary', label: 'Summary' },
                  { value: 'quotes', label: 'Quotes' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    variant={includePatientQuotes === opt.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setIncludePatientQuotes(opt.value)}
                    className="h-7 text-xs flex-1"
                    data-testid={`quick-settings-quotes-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground/70">
                {includePatientQuotes === 'summary' && 'Summarizes patient symptoms/feelings'}
                {includePatientQuotes === 'quotes' && 'Includes direct "Patient states..." quotes'}
                {includePatientQuotes === 'none' && 'Clinical language only'}
              </p>
            </div>

            {/* Clinical Tone */}
            {effectiveRole === 'provider' && (
              <div className="space-y-1.5 pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground">Clinical Tone</span>
                <div className="flex flex-wrap gap-1">
                  {[
                    { value: 'empathetic', label: 'Empathetic', tip: 'Preserves emotions, tearfulness, stress, therapeutic rapport. Best for: BH visits, grief, adjustment' },
                    { value: 'educational', label: 'Educational', tip: 'Adds MOA of meds, off-label rationale, "discussed with patient" language. Best for: med starts, chronic disease teaching' },
                    { value: 'detailed', label: 'Detailed', tip: 'MAXIMUM data — every lab value, vital, trend, risk score, med detail. Nothing summarized. Best for: complex visits, annual reviews, CHD risk' },
                    { value: 'neutral', label: 'Neutral', tip: 'Standard objective documentation, no emotional language. Best for: routine sick call, straightforward visits' },
                    { value: 'concern', label: 'Concern', tip: 'Highlights red flags, safety screens (PHQ-9, C-SSRS), escalation language. Best for: SI/HI, safety plans, high-risk' },
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      variant={clinicalTone === opt.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setClinicalTone(opt.value); localStorage.setItem('writeright_clinical_tone', opt.value); }}
                      className="h-7 text-xs flex-1"
                      title={opt.tip}
                      data-testid={`quick-settings-tone-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground/70">
                  {clinicalTone === 'empathetic' && 'Preserves emotional context, patient feelings, therapeutic rapport'}
                  {clinicalTone === 'educational' && 'Adds MOA, off-label rationale, "discussed with patient" language'}
                  {clinicalTone === 'detailed' && 'MAXIMUM detail — every lab value, vital, trend, risk score, med detail included'}
                  {clinicalTone === 'neutral' && 'Standard clinical documentation, objective only'}
                  {clinicalTone === 'concern' && 'Highlights red flags, safety screens, escalation language'}
                </p>
              </div>
            )}

            {/* A/P Format — Combined vs Split (provider only) */}
            {effectiveRole === 'provider' && setApFormat && (
              <div className="space-y-1.5 pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground">A/P Format</span>
                <div className="flex gap-1">
                  {[
                    { value: 'combined', label: 'Combined', tip: 'Diagnosis + ICD-10 + plan on each bullet' },
                    { value: 'bulleted', label: 'Bulleted', tip: 'Plan actions only — no diagnosis labels or codes' },
                    { value: 'split', label: 'Split', tip: 'Each diagnosis as separate numbered section' },
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      variant={apFormat === opt.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setApFormat(opt.value); localStorage.setItem('writeright_ap_format', opt.value); }}
                      className="h-7 text-xs flex-1"
                      title={opt.tip}
                      data-testid={`quick-settings-ap-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground/70">
                  {apFormat === 'combined' && 'Diagnosis + ICD-10 code + plan on each bullet'}
                  {apFormat === 'bulleted' && 'Plan actions only — no diagnosis labels or ICD-10 codes'}
                  {apFormat === 'split' && 'Each diagnosis as separate numbered section'}
                </p>
              </div>
            )}

            {/* Full Settings */}
            {setShowProfileModal && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProfileModal(true)}
                className="w-full h-8 text-xs gap-2 mt-1"
                data-testid="fab-full-settings"
              >
                <Settings className="w-3.5 h-3.5" />
                Full Settings & Schedule
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </>
  );
};

export default FloatingActions;
